import React, { useState, useMemo } from 'react';
import {
    IconButton,
    Chip,
    Stack,
    Box,
    Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CommonTable from '../UI/Table/CommonTable';

/**
 * DataTable component for displaying and managing data records
 */
const DataTable = ({
    tableData = [],
    handleEdit,
    handleDelete,
    isLoading = false,
    actionButtons = null,
    tabLabel = "",
    activeTab = 0
}) => {
    // State management
    const [selectedRow, setSelectedRow] = useState(null);

    // Evaluation Matrix types for chip styling
    const evaluationMatrixTypes = [
        { value: "work_ethic", label: "Work Ethic" },
        { value: "skills", label: "Skills" },
        { value: "21_century_fluencies", label: "21st Century Fluencies" },
        { value: "learning_skill_growth", label: "Learning & Skill Growth" },
        { value: "task_ownership_delivery", label: "Task Ownership & Delivery" },
        { value: "problem_solving_thinking", label: "Problem-Solving & Thinking" },
        { value: "code_quality_engineering", label: "Code Quality & Engineering Practices" },
        { value: "communication_collaboration", label: "Communication & Collaboration" },
        { value: "professional_habits", label: "Professional Habits" }
    ];

    const chipStyles = {
        work_ethic: { bg: '#e3f2fd', color: '#1976d2' },
        skills: { bg: '#f3e5f5', color: '#7b1fa2' },
        '21_century_fluencies': { bg: '#ede7f6', color: '#5e35b1' },
        learning_skill_growth: { bg: '#fff8e1', color: '#f9a825' },
        task_ownership_delivery: { bg: '#fbe9e7', color: '#d84315' },
        problem_solving_thinking: { bg: '#e8f5e9', color: '#2e7d32' },
        code_quality_engineering: { bg: '#f3f3f3', color: '#455a64' },
        communication_collaboration: { bg: '#edeff3', color: '#3949ab' },
        professional_habits: { bg: '#e0f7fa', color: '#00838f' },
    };

    // Memoized column definitions based on tab
    const columns = useMemo(() => {
        const baseColumns = [
            { key: 'serialNo', label: 'S.No', minWidth: 70, align: 'center' },
            { key: 'name', label: 'Name', minWidth: 150 },
        ];

        // Add type column for evaluation matrix
        if (activeTab === 5) {
            baseColumns.push({ key: 'type', label: 'Type', minWidth: 200 });
        }

        // Add client code column for most tabs
        if (tabLabel !== "Clients") {
            baseColumns.push({ key: 'client_code', label: 'Client Code', minWidth: 120 });
        }

        baseColumns.push(
            { key: 'created_at', label: 'Created', minWidth: 100 },
            { key: 'actions', label: 'Actions', minWidth: 100 }
        );

        return baseColumns;
    }, [activeTab, tabLabel]);

    const handleRowSelect = (row) => {
        setSelectedRow(selectedRow?.row_id === row.row_id ? null : row);
    };

    // Cell rendering
    const renderCell = (columnKey, value, row, index) => {
        switch (columnKey) {
            case 'serialNo':
                return (
                    <Box sx={{ textAlign: 'center', fontFamily: 'Poppins', fontWeight: 500 }}>
                        {index + 1}
                    </Box>
                );

            case 'name':
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: 400,
                        color: '#333',
                        fontSize: '13px',
                        fontFamily: 'Poppins'
                    }}>
                        {value || 'N/A'}
                    </Typography>
                );

            case 'type':
                if (activeTab === 5) { // Evaluation Matrix
                    const typeLabel = evaluationMatrixTypes.find(type => type.value === value)?.label || value;
                    return (
                        <Chip
                            label={typeLabel}
                            size="small"
                            sx={{
                                bgcolor: chipStyles[value]?.bg || '#eeeeee',
                                color: chipStyles[value]?.color || '#424242',
                                fontWeight: 600,
                                borderRadius: 2,
                                fontSize: '0.7rem',
                                height: 24,
                                fontFamily: 'Poppins'
                            }}
                        />
                    );
                }
                return value;

            case 'client_code':
                return value ? (
                    <Chip
                        label={value}
                        size="small"
                        sx={{
                            background: "#0a0068",
                            color: "#fff",
                            borderRadius: "6px",
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24,
                            fontFamily: 'Poppins'
                        }}
                    />
                ) : 'N/A';

            case 'created_at':
                return (
                    <Typography variant="body2" sx={{
                        fontSize: '12px',
                        color: '#666',
                        fontFamily: 'Poppins'
                    }}>
                        {value || 'N/A'}
                    </Typography>
                );

            case 'actions':
                return (
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <EditIcon sx={{ fontSize: 18, color: '#666' }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(row.row_id);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: 18, color: '#666' }} />
                        </IconButton>
                    </Stack>
                );
            default:
                return value || 'N/A';
        }
    };

    return (
        <CommonTable
            columns={columns}
            data={tableData}
            renderCustomCell={renderCell}
            onRowClick={handleRowSelect}
            selectedRow={selectedRow}
            actionButtons={actionButtons}
            isLoading={isLoading}
            searchPlaceholder={`Search ${tabLabel.toLowerCase()}...`}
        />
    );
};

export default DataTable;